import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ExternalLink,
  HelpCircle,
  Layers3,
  MessageCircle,
  PackageCheck,
  PlugZap,
  Send,
  ShieldCheck,
  Target,
  Workflow,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import { getWhatsAppLink } from '../config/contact';
import { submitLead } from '../services/api';
import { buildIndustryWhatsAppMessage } from '../utils/contactMessage';
import {
  createFallbackPackages,
  getPackageContent,
  getPricingCopy,
} from '../data/pricing';
import { getIndustryCommonCopy } from '../data/industries';
import IndustryIcon from './IndustryIcon';

const STACKED_PRICE_CODES = new Set(['AED', 'KWD']);
const LONG_INLINE_PRICE_CODES = new Set(['TRY']);

const RegionalPriceText = ({ amount, formatPrice, pricingRegion }) => {
  if (STACKED_PRICE_CODES.has(pricingRegion?.currencyCode)) {
    const formattedAmount = new Intl.NumberFormat(pricingRegion?.locale || 'en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));

    return (
      <span className="price-value flex min-w-0 max-w-full flex-col items-start text-[#00FFD1]">
        <span className="text-base font-bold leading-none md:text-lg">
          {pricingRegion.currencyCode}
        </span>
        <span className="text-3xl font-bold leading-none md:text-4xl">
          {formattedAmount}
        </span>
      </span>
    );
  }

  const formattedPrice = formatPrice(amount);
  const isLongInlinePrice =
    LONG_INLINE_PRICE_CODES.has(pricingRegion?.currencyCode) ||
    formattedPrice.replace(/\s/g, '').length >= 8;
  const amountSize = isLongInlinePrice
    ? 'text-[30px] md:text-[34px]'
    : 'text-3xl md:text-4xl';

  return (
    <span className={`price-value price-value-inline block max-w-full ${amountSize} font-bold leading-[0.95] text-[#00FFD1]`}>
      {formattedPrice}
    </span>
  );
};

const SystemVisual = ({ page, copy, isRTL }) => (
  <div className={`relative ${isRTL ? 'lg:order-1' : ''}`}>
    <div className="rounded-3xl border border-white/[0.08] bg-[#07110f]/80 p-4 shadow-[0_20px_80px_rgba(0,0,0,0.42)] backdrop-blur-xl">
      <div className="mb-4 flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-black/45 px-4 py-3">
        <div className="flex items-center gap-3">
          <img
            src="/logo-512.png"
            alt="Webouse"
            className="h-9 w-9 rounded-xl"
            loading="lazy"
          />
          <div>
            <p className="text-sm font-semibold text-white">
              {page.card.title}
            </p>
            <p className="text-xs text-white/42">
              {page.card.signal}
            </p>
          </div>
        </div>
        <span className="rounded-full border border-[#00FFD1]/25 bg-[#00FFD1]/10 px-3 py-1 text-xs font-semibold text-[#00FFD1]">
          {copy.liveScope}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {page.hero.bullets.map((bullet) => (
          <div
            key={bullet}
            className="min-h-[92px] rounded-2xl border border-white/[0.08] bg-white/[0.035] p-4"
          >
            <Check className="mb-3 h-5 w-5 text-[#00FFD1]" />
            <p className="text-sm leading-6 text-white/70">{bullet}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-2xl border border-white/[0.08] bg-black/45 p-4">
        <div className="mb-3 flex items-center justify-between gap-4">
          <p className="text-xs font-semibold uppercase text-white/42">
            {copy.integrationsTitle}
          </p>
          <PlugZap className="h-4 w-4 text-[#00FFD1]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {page.integrations.slice(0, 5).map((integration) => (
            <span
              key={integration}
              className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1.5 text-xs text-white/62"
            >
              {integration}
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const SectionHeading = ({ eyebrow, title, description }) => (
  <div className="mb-8 max-w-3xl">
    {eyebrow && (
      <p className="mb-3 text-sm font-semibold uppercase text-[#00FFD1]">
        {eyebrow}
      </p>
    )}
    <h2 className="text-3xl font-semibold leading-tight text-white md:text-4xl">
      {title}
    </h2>
    {description && (
      <p className="mt-3 text-base leading-7 text-white/56 md:text-lg">
        {description}
      </p>
    )}
  </div>
);

const DarkSelect = ({ name, value, options, onChange, labelPrefix = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedOption =
    options.find((option) => option.value === value) || options[0];
  const displayLabel = labelPrefix
    ? `${labelPrefix}: ${selectedOption?.label || value}`
    : selectedOption?.label || value;

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const chooseOption = (nextValue) => {
    onChange({ target: { name, value: nextValue } });
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={`glass-input flex min-h-[58px] items-center justify-between gap-3 text-left ${
          isOpen ? 'border-[#00FFD1]/60 shadow-[0_0_24px_rgba(0,255,209,0.18)]' : ''
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="min-w-0 truncate">{displayLabel}</span>
        <ChevronDown
          className={`h-5 w-5 flex-none text-white/70 transition-transform ${
            isOpen ? 'rotate-180 text-[#00FFD1]' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="mt-2 overflow-hidden rounded-xl border border-[#00FFD1]/25 bg-[#0b0f0e] p-1 shadow-[0_18px_50px_rgba(0,0,0,0.55),0_0_28px_rgba(0,255,209,0.12)]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => chooseOption(option.value)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left text-sm font-semibold transition-colors ${
                  isSelected
                    ? 'bg-[#00FFD1] text-black'
                    : 'text-white/78 hover:bg-white/[0.08] hover:text-white'
                }`}
              >
                <span>{option.label}</span>
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const IndustryContactForm = ({ page, copy, pricingRegion, currentLang, isRTL, trackPricingEvent }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    goal: page.formFocusOptions?.[0] || '',
    packageInterest: page.recommendedPackage,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    const message = buildIndustryWhatsAppMessage({
      language: currentLang,
      industryName: page.card.title,
      route: page.route,
      formData,
      pricingRegion,
      projectReferenceUrl: page.projectReferenceUrl,
    });

    window.open(getWhatsAppLink(message), '_blank', 'noopener,noreferrer');
    trackPricingEvent('industry_form_submitted', {
      industryId: page.id,
      packageInterest: formData.packageInterest,
    });

    try {
      await submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        business: formData.business,
        message,
        whatsapp_line: 'industry_solution',
        language: currentLang,
        pricing_region: pricingRegion.id,
        currency_code: pricingRegion.currencyCode,
      });
    } catch (error) {
      console.error('Industry lead could not be saved:', error);
      toast.warning(copy.saveWarning);
    } finally {
      setIsSubmitting(false);
    }

    toast.success(copy.opening);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-xl md:p-8"
      style={{ textAlign: isRTL ? 'right' : 'left' }}
    >
      <div className="mb-6">
        <h3 className="text-2xl font-semibold text-white">{copy.formTitle}</h3>
        <p className="mt-2 text-sm leading-6 text-white/52">
          {copy.formSubtitle}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <input
          className="glass-input"
          name="name"
          value={formData.name}
          onChange={updateField}
          placeholder={copy.fields.name}
          required
        />
        <input
          className="glass-input"
          type="email"
          name="email"
          value={formData.email}
          onChange={updateField}
          placeholder={copy.fields.email}
          required
        />
        <input
          className="glass-input"
          name="business"
          value={formData.business}
          onChange={updateField}
          placeholder={copy.fields.business}
          required
        />
        <input
          className="glass-input"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={updateField}
          placeholder={`${copy.fields.phone} (${copy.fields.optional})`}
        />
        <DarkSelect
          name="goal"
          value={formData.goal}
          onChange={updateField}
          options={page.formFocusOptions.map((option) => ({
            label: option,
            value: option,
          }))}
        />
        <DarkSelect
          name="packageInterest"
          value={formData.packageInterest}
          onChange={updateField}
          labelPrefix={copy.fields.packageInterest}
          options={['LAUNCH', 'BUILD', 'SCALE'].map((packageName) => ({
            label: packageName,
            value: packageName,
          }))}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-glass-primary mt-5 w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-black border-t-transparent" />
        ) : (
          <>
            <Send className="h-5 w-5" />
            {copy.fields.submit}
          </>
        )}
      </button>
    </form>
  );
};

const IndustryPageTemplate = ({ page }) => {
  const { currentLang, isRTL } = useLanguage();
  const {
    pricingRegion,
    isPricingReady,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const { addPackage, setIsCartOpen, selectedPackage } = useCart();
  const copy = getIndustryCommonCopy(currentLang);
  const pricingCopy = getPricingCopy(currentLang);
  const packages = useMemo(
    () => createFallbackPackages().filter((pkg) => ['LAUNCH', 'BUILD', 'SCALE'].includes(pkg.name)),
    []
  );

  const scrollToPackages = () => {
    document.getElementById('industry-packages')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handlePackageSelect = (pkg) => {
    const pricedPackage = localizeItemPrice(pkg, 'PACKAGE');
    const details = getPackageContent(pkg.name, currentLang);

    addPackage({
      id: pkg.id,
      canonicalName: pkg.name,
      name: details?.name || pkg.name,
      price: Number(pricedPackage.price || 0),
      basePriceUsd: Number(pkg.price || 0),
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
      features: details?.impacts || [],
      isCustom: false,
      source: page.id,
    });
    trackPricingEvent('industry_package_selected', {
      industryId: page.id,
      itemKey: pkg.name,
      displayedAmount: Number(pricedPackage.price || 0),
    });
    setIsCartOpen(true);
  };

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <section className="relative overflow-hidden bg-black pt-20">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto grid min-h-[720px] max-w-[1500px] items-center gap-12 px-5 py-16 md:px-[6%] lg:grid-cols-[1.05fr_0.95fr]">
          <div className={`relative z-10 ${isRTL ? 'lg:order-2' : ''}`}>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-4 py-2 text-sm font-semibold text-white/62 backdrop-blur-xl">
              <IndustryIcon icon={page.icon} className="h-4 w-4 text-[#00FFD1]" />
              {page.hero.eyebrow}
            </div>

            <h1 className="max-w-5xl text-4xl font-semibold leading-tight text-white md:text-6xl lg:text-7xl">
              {page.hero.title}
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-8 text-white/62 md:text-lg">
              {page.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={getWhatsAppLink(
                  buildIndustryWhatsAppMessage({
                    language: currentLang,
                    industryName: page.card.title,
                    route: page.route,
                    formData: {
                      business: '',
                      name: '',
                      email: '',
                      goal: page.formFocusOptions?.[0] || '',
                      packageInterest: page.recommendedPackage,
                    },
                    pricingRegion,
                    projectReferenceUrl: page.projectReferenceUrl,
                  })
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glass-primary w-full sm:w-auto"
                onClick={() =>
                  trackPricingEvent('industry_whatsapp_opened', {
                    industryId: page.id,
                  })
                }
              >
                <MessageCircle className="h-5 w-5" />
                {copy.heroPrimaryCta}
              </a>
              <button
                type="button"
                onClick={scrollToPackages}
                className="btn-glass-secondary w-full sm:w-auto"
              >
                <PackageCheck className="h-5 w-5 text-[#00FFD1]" />
                {copy.heroSecondaryCta}
              </button>
            </div>

            {page.projectReferenceUrl && (
              <a
                href={page.projectReferenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#00FFD1] hover:text-white"
              >
                {page.projectReferenceUrl}
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          <SystemVisual page={page} copy={copy} isRTL={isRTL} />
        </div>
      </section>

      <section className="border-y border-white/[0.07] bg-[#050706] py-10">
        <div className="mx-auto grid max-w-[1400px] gap-3 px-5 md:grid-cols-4 md:px-[7.6923%]">
          {page.trust.map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.025] px-4 py-4"
            >
              <ShieldCheck className="h-5 w-5 flex-none text-[#00FFD1]" />
              <span className="text-sm leading-6 text-white/66">{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-[7.6923%] lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow={copy.problemsTitle} title={page.card.signal} />
            <div className="space-y-3">
              {page.problems.map((problem) => (
                <div
                  key={problem}
                  className="flex gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4"
                >
                  <Target className="mt-1 h-5 w-5 flex-none text-[#00FFD1]" />
                  <p className="text-sm leading-6 text-white/62">{problem}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <SectionHeading eyebrow={copy.solutionsTitle} title={page.packageFit.title} />
            <div className="space-y-3">
              {page.solutions.map((solution) => (
                <div
                  key={solution.title}
                  className="rounded-2xl border border-[#00FFD1]/15 bg-[#00FFD1]/[0.035] p-5"
                >
                  <h3 className="text-lg font-semibold text-white">
                    {solution.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-white/58">
                    {solution.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#050706] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-[7.6923%]">
          <SectionHeading title={copy.outcomesTitle} description={page.hero.subtitle} />
          <div className="grid gap-4 md:grid-cols-3">
            {page.outcomes.map((outcome) => (
              <div
                key={outcome.label}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6"
              >
                <Layers3 className="mb-5 h-6 w-6 text-[#00FFD1]" />
                <h3 className="text-xl font-semibold text-white">{outcome.label}</h3>
                <p className="mt-3 text-sm leading-6 text-white/58">{outcome.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-[7.6923%] lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <SectionHeading
              eyebrow={copy.integrationsTitle}
              title={copy.processTitle}
              description={copy.packageSubtitle}
            />
            <div className="flex flex-wrap gap-2">
              {page.integrations.map((integration) => (
                <span
                  key={integration}
                  className="rounded-full border border-white/[0.09] bg-white/[0.035] px-4 py-2 text-sm text-white/64"
                >
                  {integration}
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {page.process.map((step, index) => (
              <div
                key={step.title}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#00FFD1] text-sm font-bold text-black">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <Workflow className="h-5 w-5 text-[#00FFD1]" />
                </div>
                <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-6 text-white/55">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="industry-packages" className="bg-[#050706] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-[7.6923%]">
          <SectionHeading
            title={copy.packageTitle}
            description={page.packageFit.body}
          />
          <div className="grid gap-5 md:grid-cols-3">
            {packages.map((pkg) => {
              const details = getPackageContent(pkg.name, currentLang);
              const pricedPackage = localizeItemPrice(pkg, 'PACKAGE');
              const isRecommended = pkg.name === page.recommendedPackage;
              const isSelected = selectedPackage?.canonicalName === pkg.name;

              return (
                <div
                  key={pkg.name}
                  className={`relative rounded-3xl border p-6 ${
                    isRecommended
                      ? 'border-[#00FFD1]/35 bg-[#00FFD1]/[0.06]'
                      : 'border-white/[0.08] bg-white/[0.025]'
                  }`}
                >
                  {isRecommended && (
                    <span className="mb-4 inline-flex rounded-lg bg-[#00FFD1] px-3 py-1 text-xs font-bold uppercase text-black">
                      {copy.packageLabels.recommended}
                    </span>
                  )}
                  <h3 className="text-xl font-semibold text-white">
                    {details?.name || pkg.name}
                  </h3>
                  <p className="mt-3 min-h-[74px] text-sm leading-6 text-white/55">
                    {details?.target || page.packageFit.title}
                  </p>
                  <div className="my-6">
                    <span className="mb-2 block text-sm text-white/42">
                      {pricingCopy.startingAt}
                    </span>
                    {isPricingReady ? (
                      <RegionalPriceText
                        amount={pricedPackage.price}
                        formatPrice={formatPrice}
                        pricingRegion={pricingRegion}
                      />
                    ) : (
                      <span
                        className="block h-12 w-36 animate-pulse rounded bg-[#00FFD1]/10"
                        role="status"
                        aria-label="Loading regional price"
                      />
                    )}
                    <span className="mt-1 block text-sm text-white/40">
                      {pricingCopy.oneTime}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handlePackageSelect(pkg)}
                    disabled={!isPricingReady}
                    className={`w-full ${
                      isRecommended ? 'btn-glass-primary' : 'btn-glass-secondary'
                    } disabled:cursor-wait disabled:opacity-50`}
                  >
                    {isSelected ? pricingCopy.selected : pricingCopy.selectPackage}
                  </button>
                </div>
              );
            })}
          </div>

          {page.spotlightCta && (
            <div className="mt-8 rounded-3xl border border-[#00FFD1]/25 bg-[#00FFD1]/[0.045] p-6 md:flex md:items-center md:justify-between md:gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-white">
                  {page.spotlightCta.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-white/58">
                  {page.spotlightCta.body}
                </p>
              </div>
              <Link
                to={page.spotlightCta.route}
                className="btn-glass-primary mt-5 w-full md:mt-0 md:w-auto"
              >
                {copy.fivemCta}
                <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-[7.6923%] lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHeading
              eyebrow={copy.faqTitle}
              title={copy.finalTitle}
              description={copy.finalSubtitle}
            />
            <a
              href={getWhatsAppLink(
                buildIndustryWhatsAppMessage({
                  language: currentLang,
                  industryName: page.card.title,
                  route: page.route,
                  formData: {
                    business: '',
                    name: '',
                    email: '',
                    goal: page.formFocusOptions?.[0] || '',
                    packageInterest: page.recommendedPackage,
                  },
                  pricingRegion,
                  projectReferenceUrl: page.projectReferenceUrl,
                })
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-glass-primary"
            >
              <MessageCircle className="h-5 w-5" />
              {copy.heroPrimaryCta}
            </a>
          </div>

          <div className="space-y-3">
            {page.faq.map((item) => (
              <details
                key={item.question}
                className="group rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold text-white">
                  <span>{item.question}</span>
                  <HelpCircle className="h-5 w-5 flex-none text-[#00FFD1]" />
                </summary>
                <p className="mt-3 text-sm leading-6 text-white/58">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.07] bg-[#050706] py-20 md:py-28">
        <div className="mx-auto max-w-[1100px] px-5 md:px-[7.6923%]">
          <IndustryContactForm
            page={page}
            copy={copy}
            pricingRegion={pricingRegion}
            currentLang={currentLang}
            isRTL={isRTL}
            trackPricingEvent={trackPricingEvent}
          />
        </div>
      </section>
    </div>
  );
};

export default IndustryPageTemplate;
